import { defineAsyncComponent, h, type Component, type ComputedRef, type InjectionKey } from 'vue'

import type { IScreen } from '@/interfaces/gui'

/**
 * Rendered when a screen's chunk cannot be fetched. Every screen below is
 * loaded on demand, and a rejected loader renders nothing at all by default —
 * an accordion panel would open onto a blank area with no hint why.
 */
const ScreenLoadError: Component = {
  name: 'ScreenLoadError',
  // Without this the failed screen's props and the raw error object land in the
  // DOM as fallthrough attributes.
  inheritAttrs: false,
  setup() {
    return () =>
      h(
        'div',
        { class: 'screen-load-error', style: 'padding:1rem;text-align:center;opacity:0.8' },
        [
          h('div', { class: 'error-icon' }, '⚠️'),
          h('div', { class: 'error-message' }, 'Screen failed to load'),
        ],
      )
  },
}

type ScreenLoader = () => Promise<{ default: Component }>

const lazyScreen = (loader: ScreenLoader) =>
  defineAsyncComponent({ loader, errorComponent: ScreenLoadError, timeout: 15_000 })

/**
 * Configuration-backed screen types that dinoConnect can render.
 *
 * Standalone routes and ViewPager children deliberately share this registry so
 * a screen component cannot be available in only one of the two locations.
 */
export const screenComponents = {
  viewPager: lazyScreen(() => import('@/components/ViewPager.vue')),
  tinyparameterPage: lazyScreen(() => import('@/components/TinyParameterPage.vue')),
  parameterPage: lazyScreen(() => import('@/components/ParameterPage.vue')),
  chips: lazyScreen(() => import('@/components/Chips.vue')),
  attractions: lazyScreen(() => import('@/components/Attractions.vue')),
  listview: lazyScreen(() => import('@/components/ListViewScreen.vue')),
  timer: lazyScreen(() => import('@/components/TimerPage.vue')),
  eventtimer: lazyScreen(() => import('@/components/EventTimer.vue')),
  dmx: lazyScreen(() => import('@/components/DmxPage.vue')),
  potCalibration: lazyScreen(() => import('@/components/PotCalibrationPage.vue')),
  trend: lazyScreen(() => import('@/components/TrendPage.vue')),
} satisfies Partial<Record<IScreen['type'], Component>>

export type SupportedScreenType = keyof typeof screenComponents

export const supportedScreenTypes = Object.keys(
  screenComponents,
) as ReadonlyArray<SupportedScreenType>

export function getScreenComponent(type: string): Component | undefined {
  if (Object.prototype.hasOwnProperty.call(screenComponents, type)) {
    return screenComponents[type as SupportedScreenType]
  }

  return undefined
}

/**
 * Screen names already being rendered further up the tree. A viewPager can now
 * contain another viewPager, so a page pointing back into its own chain would
 * recurse until the stack overflows.
 */
export const screenAncestorsKey = Symbol('screenAncestors') as InjectionKey<
  ComputedRef<ReadonlySet<string>>
>
